/**
 * pets.ts — pet listing/adoption/state + bond + wishes + pokedex
 * (PR-15 split of api/client.ts).
 *
 * Owns everything that addresses a specific pet (or the pet collection):
 * adopt new, switch active, mutate hunger/happiness via state, log a tap
 * for bond XP, fetch the current bond row + unlocks ladder, idle bubble,
 * active wish + wish check, and the Pokedex (breed encyclopedia) view.
 *
 * 调用关系：
 *   - Merged into the unified `api` object via api/client.ts.
 *   - Heavy users: HomePage / ParkPage (3D scene + interact), PetProfilePage,
 *     PetWishCard, BondBadge, PokedexPage.
 *   - Backend: backend/src/routes/pets.js, bond.js, wishes.js, pokedex.js.
 *
 * 关键约束：
 *   - petInteract forwards getTz() because the backend's per-day bond cap
 *     resets at midnight in the child's IANA tz.
 *   - wishCheck must run after every purchase/feed; backend uses item_db_id
 *     for inventory items and item_code for shop SKUs — the caller picks
 *     which to send based on context.
 */
import type { Breed, Pet, PetState } from '@xiaopeiban/shared';
import { call, getTz } from './_core';
// Type-only import to avoid an ESM cycle with shop.ts at module-eval time
// (shop.ts also pulls bond types from this file). TypeScript erases
// type-only imports at build time so no runtime require() is emitted.
import type { WishCheckResponse } from './shop';

// ─── v0.10.0 Bond / Wish types ─────────────────────────────────────────────

export interface BondLevelUp {
  new_level: number;
  zh?: string;
  en?: string;
  unlock?: string | null;
}

export interface BondGainResponse {
  xp: number;
  level: number;
  level_up: BondLevelUp | null;
}

export interface PetBondRow {
  pet_id: number;
  bond_xp: number;
  bond_level: number;
  bond_title_zh: string;
  bond_title_en: string;
  last_interacted_at: number | null;
  total_taps: number;
  total_feeds: number;
  total_chats: number;
  total_plays: number;
  total_wishes: number;
}

export interface BondUnlock {
  level: number;
  title_zh: string;
  title_en: string;
  unlock_feature: string | null;
  xp_required: number;
  unlocked: boolean;
}

export interface PetWish {
  id: number;
  kind: 'food' | 'toy' | 'decor';
  item_code: string;
  item_db_id: number | null;
  text_zh: string;
  text_en: string;
  generated_at: number;
  expires_at: number;
  reward_bond_xp: number;
  reward_sen: number;
  item: {
    id: number; code: string; display_name: string;
    price: number; sprite_key: string | null; category: string;
  } | null;
}

export interface PetPersona {
  nickname?: string;
  traits?: string[];
  speech_style?: string;
  favorite_foods?: string[];
  fears?: string[];
  quirks?: string[];
  greeting_phrases?: string[];
  birthday?: string;
}

export interface PokedexBreed {
  id: number;
  species: string;
  breed_name: string;
  rarity: string;
  price: number;
  /** null when unowned — server hides the artwork until the child has met one. */
  sprite_key: string | null;
  unlock_hint_zh: string | null;
  unlock_hint_en: string | null;
  owned: boolean;
  /** Earliest adopted_at timestamp across all pets the child has had of this breed. */
  first_owned_at: number | null;
  /** Lifetime count of user-role chat messages across owned pets of this breed. */
  total_interactions: number;
}

export const petsApi = {
  listPets() {
    return call<{ pets: Pet[] }>('/api/v1/child/pets');
  },
  listBreeds() {
    return call<{ breeds: Breed[] }>('/api/v1/child/breeds');
  },
  adoptPet(breed_id: number, nickname: string) {
    return call<{ ok: true; pet: Pet; balance: number }>(
      '/api/v1/child/pets/adopt',
      { method: 'POST', body: JSON.stringify({ breed_id, nickname }) },
    );
  },
  updatePetState(petId: number, state: PetState) {
    return call<{ ok: true }>(`/api/v1/child/pets/${petId}/state`, {
      method: 'PUT', body: JSON.stringify({ state }),
    });
  },
  setActivePet(petId: number) {
    return call<{ ok: true; active_pet_id: number }>(`/api/v1/child/pets/${petId}/active`, {
      method: 'PUT',
    });
  },

  // ─── v0.10.0 Bond / Interaction / Wishes ───────────────────────────────
  petInteract(petId: number, type: 'tap' | 'long_press' | 'pet_stroke') {
    return call<{
      ok: true; throttled?: boolean;
      bond_xp_gained: number;
      bond_xp?: number; bond_level?: number;
      bond_capped?: boolean;
      bond_level_up?: BondLevelUp | null;
      pet_response: { mood: string; emoji: string; text_zh: string; text_en: string };
    }>(`/api/v1/child/pets/${petId}/interact`, {
      method: 'POST', body: JSON.stringify({ type, tz: getTz() }),
    });
  },
  petBond(petId: number) {
    return call<{
      ok: true;
      pet: { id: number; nickname: string; species: string; breed_name: string };
      bond: PetBondRow;
      next_level: { level: number; xp_required: number } | null;
      unlocks: BondUnlock[];
      daily_hard_cap: number;
    }>(`/api/v1/child/pets/${petId}/bond`);
  },
  petIdleBubble(petId: number) {
    return call<{
      ok: true;
      bubble: { kind: 'hungry' | 'wantPlay' | 'snuggle'; text_zh: string; text_en: string };
    }>(`/api/v1/child/pets/${petId}/idle-bubble`);
  },
  wishActive() {
    return call<{
      wish: PetWish | null;
      reason?: string;
      pet?: { id: number; nickname: string; species: string };
    }>('/api/v1/child/pets/wishes/active');
  },
  wishCheck(reason: 'purchase' | 'feed', itemDbId?: number, itemCode?: string) {
    return call<WishCheckResponse>('/api/v1/child/pets/wishes/check', {
      method: 'POST',
      body: JSON.stringify({ reason, item_id: itemDbId, item_code: itemCode, tz: getTz() }),
    });
  },

  // Pokedex (PR-4: breed encyclopedia)
  pokedex() {
    return call<{ breeds: PokedexBreed[] }>('/api/v1/child/pokedex');
  },
};
